<script>
import { mapGetters } from 'vuex';

export default {
  name: 'WordsLimitProgressBar',
  props: {
    // Word count from the current knowledge base
    wordCount: {
      type: Number,
      required: true,
      default: 0
    },
    
    // Optional: Knowledge base name for display in the progress bar
    knowledgeBaseName: {
      type: String,
      required: false,
      default: ''
    }
  },  
  computed: {
    ...mapGetters('stripe', ['getWordsLimit']),
    
    // Get the total word count for this knowledge base
    totalWordCount() {
      return this.wordCount || 0;
    },
    
    // Get the allowed word limit from the subscription
    wordLimit() {
      return this.getWordsLimit || 100000; // Default fallback value
    },
    
    // Calculate the percentage of the word limit that's been used
    usagePercentage() {
      if (!this.wordLimit) return 0;
      return Math.min(100, Math.round((this.totalWordCount / this.wordLimit) * 100));
    },
    
    // Get class based on usage percentage
    progressClass() {
      if (this.usagePercentage >= 90) return 'bg-red-500';
      if (this.usagePercentage >= 75) return 'bg-yellow-500';
      return 'bg-green-500';
    },
    
    // Get text color based on usage percentage
    textClass() {
      if (this.usagePercentage >= 90) return 'text-red-600 dark:text-red-400';
      if (this.usagePercentage >= 75) return 'text-yellow-600 dark:text-yellow-400';
      return 'text-green-600 dark:text-green-400';
    },
    
    // Display title with knowledge base name if provided
    displayTitle() {
      return this.knowledgeBaseName 
        ? `${this.knowledgeBaseName} - ${this.$t('KNOWLEDGE.WORD_LIMIT_PROGRESS_BAR.WORD_USAGE')}` 
        : this.$t('KNOWLEDGE.WORD_LIMIT_PROGRESS_BAR.PLACEHOLDER_WORD_USAGE');
    },
    
    // Format large numbers with commas
    formattedWordLimit() {
      const raw = this.wordLimit ?? 0;
      const value = Number(raw);

      return Number.isFinite(value) ? value.toLocaleString() : '0';
    },

    formattedWordCount() {
      const raw = this.totalWordCount ?? 0;
      const value = Number(raw);

      return Number.isFinite(value) ? value.toLocaleString() : '0';
    }
  }
}
</script>

<template>
  <div class="word-limit-progress-container bg-white dark:bg-slate-800/30 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-2">
      <h3 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 sm:mb-0">
        {{ displayTitle }}
      </h3>
      <div class="text-sm" :class="textClass">
        <span class="font-medium" data-testid="document_dataset_word_count">{{ formattedWordCount }}</span> / {{ formattedWordLimit }} {{$t('KNOWLEDGE.WORD_COUNT_PLURAL')}}
        <span class="ml-1 text-xs">({{ usagePercentage }}%)</span>
      </div>
    </div>
    
    <div class="relative h-2 w-full bg-slate-200 dark:bg-slate-700 rounded overflow-hidden">
      <div 
        class="absolute top-0 left-0 h-full transition-all duration-500 ease-out rounded"
        :class="progressClass"
        :style="{ width: `${usagePercentage}%` }"
        role="progressbar" 
        :aria-valuenow="usagePercentage" 
        aria-valuemin="0" 
        aria-valuemax="100"
      ></div>
    </div>
    
    <p v-if="usagePercentage >= 90" class="mt-2 text-xs text-red-600 dark:text-red-400">
      <span class="font-medium">{{ $t('KNOWLEDGE.WORD_LIMIT_PROGRESS_BAR.USAGE.WARNING_LABEL') }}</span> {{ $t('KNOWLEDGE.WORD_LIMIT_PROGRESS_BAR.USAGE.WARNING_MESSAGE') }}
    </p>
    <p v-else-if="usagePercentage >= 75" class="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
      <span class="font-medium">{{ $t('KNOWLEDGE.WORD_LIMIT_PROGRESS_BAR.USAGE.NOTE_LABEL') }}</span> {{ $t('KNOWLEDGE.WORD_LIMIT_PROGRESS_BAR.USAGE.NOTE_MESSAGE') }}
    </p>
    <p v-else class="mt-2 text-xs text-slate-500 dark:text-slate-400">
      {{ $t('KNOWLEDGE.WORD_LIMIT_PROGRESS_BAR.USAGE.EFFICIENT_USAGE') }}
    </p>
  </div>
</template>